import React, { useState } from 'react';
import {
  TextField,
  Button,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  Box,
  Snackbar,
  Card,
  CardContent,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { apiServices } from '../../../../services/apiServices';
import { logout } from '../../../../store/slices/authSlice';

const ChangePassword = () => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const { user } = useSelector((state) => state.auth);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear validation errors when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: null }));
    }

    // Clear general error when user starts typing
    if (error) {
      setError(null);
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.currentPassword.trim()) {
      errors.currentPassword = 'Current password is required';
    }

    if (!formData.newPassword.trim()) {
      errors.newPassword = 'New password is required';
    } else if (formData.newPassword.length < 7) {
      errors.newPassword = 'New password must be at least 7 characters long';
    }

    if (!formData.confirmPassword.trim()) {
      errors.confirmPassword = 'Please confirm your new password';
    } else if (formData.newPassword !== formData.confirmPassword) {
      errors.confirmPassword = 'New Password and Confirm Password do not match';
    }

    if (formData.currentPassword === formData.newPassword) {
      errors.newPassword = 'New password must be different from current password';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!user?.username) {
      setError('User information not found. Please refresh the page and try again.');
      return;
    }

    if (!user?.email) {
      setError('User email not found. Please contact support to update your email address.');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const payload = {
        username: user.email,
        // emailid: user.email, // Use emailid as expected by the API
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword
      };

      const response = await apiServices.putAPI('update-password', payload);

      if (response && response.status >= 200 && response.status < 300) {
        console.log('Password updated successfully:', response.data);
        setSuccess(true);
        // Reset form
        setFormData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setValidationErrors({});

        // Clear any stored user data from localStorage
        localStorage.removeItem('user');
        localStorage.removeItem('otp');
        localStorage.removeItem('basicAuth');

        // Logout and redirect after a short delay to show success message
        setTimeout(() => {
          dispatch(logout());
          navigate('/login');
        }, 2000);
      } else {
        const errorMessage = response?.data?.message || `HTTP ${response?.status}: ${response?.statusText}`;
        throw new Error(errorMessage);
      }
    } catch (err) {
      console.error('Error updating password:', err);

      // Handle specific error cases
      let errorMessage = 'Failed to update password. Please try again.';

      if (err.response?.status === 401) {
        errorMessage = 'Current password is incorrect. Please check and try again.';
      } else if (err.response?.status === 400) {
        errorMessage = err.response?.data?.message || 'Invalid request. Please check your input.';
      } else if (err.response?.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSuccess(false);
    setError(null);
  };

  return (
    <Grid container sx={{
      // maxWidth: { xs: '100%', sm: 600, md: 800 }, 
      mx: 'auto',
      // p: { xs: 2, sm: 3 },
      minHeight: 'auto',
      justifyContent: 'flex-start'
    }}>
      {/* Header Section */}
      <Grid size={{ xs: 12, md: 12 }} sx={{
        textAlign: 'left',
        // px: { xs: 1, sm: 2 }
      }}>


        {/* Information Notice */}
        <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Update your account password to keep your profile secure. Please ensure you use a strong, unique
            password.
          </Typography>
        </Box>
      </Grid>

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            borderRadius: 2,
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }}
        >
          {error}
        </Alert>
      )}

      {/* Success Alert */}
      {success && (
        <Alert
          severity="success"
          sx={{
            mb: 2,
            borderRadius: 2,
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }}
        >
          Password updated successfully! You will be logged out and redirected to the login page. Please log in with your new password.
        </Alert>
      )}

      {/* Main Form Card */}

      <form onSubmit={handleSubmit} className='w-100 mt-10px'>
        <Grid container spacing={2}>
          {/* Current Password - Full Width */}
          <Grid size={{ xs: 12, md: 4 }}>

            <Typography sx={{ textAlign: "left", mb: 1 }}>Current Password</Typography>
            <TextField
              fullWidth
              name="currentPassword"
              type="password"
              placeholder="Enter your current password"
              variant="outlined"
              margin="dense"
              size="small"
              sx={{ mb: 1 }}
              required
              value={formData.currentPassword}
              onChange={handleChange}
              error={!!validationErrors.currentPassword}
              helperText={validationErrors.currentPassword}
              disabled={loading}

            />
          </Grid>

          {/* New Password - Full Width */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography sx={{ textAlign: "left", mb: 1 }}>
              New Password
            </Typography>
            <TextField
              fullWidth
              name="newPassword"
              type="password"
              placeholder="Enter new password"
              variant="outlined"

              required
              value={formData.newPassword}
              onChange={handleChange}
              error={!!validationErrors.newPassword}
              helperText={validationErrors.newPassword || 'Minimum 7 characters'}
              disabled={loading}

              margin="dense"
              size="small"
              sx={{ mb: 1 }}
            />
          </Grid>

          {/* Confirm New Password - Full Width */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography sx={{ textAlign: "left", mb: 1 }}>
              Confirm New Password
            </Typography>
            <TextField
              fullWidth
              name="confirmPassword"
              type="password"
              placeholder="Confirm new password"
              variant="outlined"

              required
              value={formData.confirmPassword}
              onChange={handleChange}
              error={!!validationErrors.confirmPassword}
              helperText={validationErrors.confirmPassword}
              disabled={loading}
              margin="dense"
              size="small"
              sx={{ mb: 1 }}
            />
          </Grid>


        </Grid>
        <Grid container spacing={2} justifyContent={{ xs: 'center', sm: 'flex-end' }}>
          {/* Button Section - Full Width, Centered and Prominent */}
          <Grid size xs={12}>
            <Grid container sx={{
              justifyContent: { xs: 'center', sm: 'flex-end' },
              pt: 2,
              mb: 3,
            }}>
              <Grid size={12}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={isMobile ? 18 : 20} color="inherit" /> : null}
                  sx={{
                    background: 'linear-gradient(to right, #214F7D, #3A8139)',
                    color: 'white',
                    borderRadius: "50px"
                  }}
                >
                  {loading ? 'Updating...' : 'Update Password'}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </form>


      {/* Password Requirements Info */}
      <Card mt={4} sx={{
        borderRadius: { xs: 2, sm: 3 },
        boxShadow: {
          xs: '0 1px 6px rgba(0,0,0,0.08)',
          sm: '0 2px 12px rgba(0,0,0,0.06)'
        },
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'
      }}>
        <CardContent sx={{
          p: { xs: 2, sm: 2.5 },
          '&:last-child': { pb: { xs: 2, sm: 2.5 } }
        }}>
          <Typography

            gutterBottom
            sx={{
              fontWeight: '4400',
              color: 'text.primary',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              fontSize: { xs: '1.0rem', sm: '1.1rem' }
            }}
          >
            🔒 Password Requirements
          </Typography>
          <Grid container sx={{ pl: { xs: 1, sm: 2 } }}>
            <Grid size={12}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mb: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  fontSize: { xs: '0.8rem', sm: '0.875rem' }
                }}
              >
                <span style={{ color: '#3A8139' }}>✓</span> Minimum 7 characters
              </Typography>
            </Grid>
            <Grid size={12}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mb: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  fontSize: { xs: '0.8rem', sm: '0.875rem' }
                }}
              >
                <span style={{ color: '#3A8139' }}>✓</span> Must be different from your current password
              </Typography>
            </Grid>
            <Grid size={12}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mb: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  fontSize: { xs: '0.8rem', sm: '0.875rem' }
                }}
              >
                <span style={{ color: '#3A8139' }}>✓</span> For security, avoid using easily guessable information
              </Typography>
            </Grid>
            <Grid size={12}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  fontSize: { xs: '0.8rem', sm: '0.875rem' }
                }}
              >
                <span style={{ color: '#3A8139' }}>✓</span> Use a mix of letters, numbers and symbols
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Snackbar for additional feedback */}
      <Snackbar
        open={success || !!error}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{
          vertical: isMobile ? 'bottom' : 'bottom',
          horizontal: 'center'
        }}
        sx={{
          '& .MuiSnackbar-root': {
            bottom: { xs: 16, sm: 24 }
          }
        }}
      >
        {success ? (
          <Alert
            onClose={handleCloseSnackbar}
            severity="success"
            sx={{
              width: '100%',
              borderRadius: 2,
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          >
            Password updated successfully! Redirecting to login...
          </Alert>
        ) : error ? (
          <Alert
            onClose={handleCloseSnackbar}
            severity="error"
            sx={{
              width: '100%',
              borderRadius: 2,
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          >
            {error}
          </Alert>
        ) : null}
      </Snackbar>
    </Grid>
  );
};

export default ChangePassword;

